import { Component, OnInit, inject } from '@angular/core';
import { AnnouncementManagementService } from '../services/announcement-management.service';
import { Announcement } from '../models/announcement';
import { AuthService } from '../services/auth.service';
import { MessageService } from 'primeng/api';
import { ILogger, LOGGER_TOKEN } from 'src/app/services/logger/ilogger';

@Component({
    selector: 'app-notification-management',
    templateUrl: './notification-management.component.html',
    styleUrls: ['./notification-management.component.css'],
    standalone: false
})
export class NotificationManagementComponent implements OnInit {
  private readonly logger = inject<ILogger>(LOGGER_TOKEN);
  private readonly announcementManagementService = inject(AnnouncementManagementService);
  private readonly messageService = inject(MessageService);
  private readonly authService = inject(AuthService);

  cols = [
    { field: 'startDate', header: 'Start Date' },
    { field: 'endDate', header: 'End Date' },
    { field: 'notification_name', header: 'Announcement' },
    { field: 'description', header: 'Description' },
    { field: 'authorName', header: 'Author' },
    { field: 'Actions', header: 'Actions' },
  ];
  inactiveCols = this.cols
    .filter((column) => column.field !== 'Actions')
    .map((column) => column.field === 'endDate'
      ? { ...column, field: 'archiveDate', header: 'Archive Date' }
      : column);
  get isDdpd(): boolean {
    return this.authService.user?.role === 'DDPD';
  }
  announcementId: string | null = null;
  announcementAddUpdate = false;
  activeRecords: Announcement[] = [];
  inactiveRecords: Announcement[] = [];
  scope: Partial<Announcement> = {
    startDate: null,
    endDate: null,
    notification_name: null,
    description: null,
  };
  isValid = false;

  ngOnInit(): void {
    const body = document.getElementsByTagName('body')[0];
    body.className = '';
    body.classList.add('subpage');
    this.updateAnnouncementList();
  }

  updateAnnouncementList(): void {
    this.loadRecords('active');
    if (this.isDdpd) {
      this.loadRecords('inactive');
    } else {
      this.inactiveRecords = [];
    }
  }

  private loadRecords(status: 'active' | 'inactive'): void {
    this.announcementManagementService.getAnnouncements(0, 1000, status).subscribe({
      next: (response) => {
        if (status === 'active') {
          this.activeRecords = response.docs;
        } else {
          this.inactiveRecords = response.docs;
        }
      },
      error: (error) => this.logger.exception(error),
    });
  }

  showAnnouncementAdd(): void {
    if (!this.isDdpd) {
      return;
    }
    this.messageService.clear();
    this.announcementId = null;
    this.scope = { startDate: this.getDefaultStartDate(), endDate: null, notification_name: null, description: null };
    this.isValid = false;
    this.announcementAddUpdate = true;
  }

  validateForm(): void {
    this.isValid = Boolean(this.scope.notification_name && this.scope.description);
  }

  submitAnnouncement(): void {
    if (!this.isDdpd) {
      return;
    }
    this.announcementManagementService.saveAnnouncement(this.scope, this.announcementId).subscribe({
      next: () => {
        const messageText = this.announcementId === null
          ? 'Announcement Saved Successfully'
          : 'Announcement Updated Successfully';
        this.announcementAddUpdate = false;
        this.updateAnnouncementList();
        this.messageService.add({ severity: 'success', life: 4000, summary: messageText, detail: '' });
      },
      error: (error) => {
        this.logger.exception(error);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Unable to save announcement', detail: '' });
      },
    });
  }

  showUpdateAnnouncement(announcement: Announcement): void {
    if (!this.canEdit(announcement)) {
      return;
    }
    this.messageService.clear();
    this.announcementId = announcement._id;
    this.scope = {
      startDate: announcement.startDate ? this.toLocalCalendarDate(announcement.startDate) as unknown as string : this.getDefaultStartDate(),
      endDate: announcement.endDate ? this.toLocalCalendarDate(announcement.endDate) as unknown as string : null,
      notification_name: announcement.notification_name,
      description: announcement.description,
    };
    this.validateForm();
    this.announcementAddUpdate = true;
  }

  archiveAnnouncement(row: Announcement): void {
    if (!this.canArchive(row) || !confirm('Are you sure that you want to archive this announcement?')) {
      return;
    }
    this.announcementManagementService.archiveAnnouncement(row._id).subscribe({
      next: () => {
        this.updateAnnouncementList();
        this.messageService.add({ severity: 'success', life: 4000, summary: 'Archived Successfully', detail: '' });
      },
      error: (error) => {
        this.logger.exception(error);
        this.messageService.add({ severity: 'error', sticky: true, summary: 'Unable to archive announcement', detail: '' });
      },
    });
  }

  canEdit(row: Announcement): boolean {
    return this.isDdpd &&
      row.source !== 'ingest' &&
      !row.archivedAt &&
      Boolean(row.authorId) &&
      String(row.authorId) === String(this.authService.user?._id);
  }

  canArchive(row: Announcement): boolean {
    if (!this.isDdpd) {
      return false;
    }
    if (row.archivedAt) {
      return false;
    }

    if (row.source === 'ingest' || !row.authorId) {
      return true;
    }

    return String(row.authorId) === String(this.authService.user?._id);
  }

  private getDefaultStartDate(): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today as unknown as string;
  }

  private toLocalCalendarDate(value: string): Date {
    const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (calendarDate) {
      return new Date(Number(calendarDate[1]), Number(calendarDate[2]) - 1, Number(calendarDate[3]));
    }
    return new Date(value);
  }
}
